import { useState } from "react";
import { ArrowUp, X } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import { setLenis } from "@/lib/scroll";

const LEGAL = {
  impressum: {
    title: "Impressum",
    body: "Leno Fotografie — Eric Leno\nUlm, Deutschland (privates Hobby-Projekt)\n\nEinsatzgebiet: Ulm, Neu-Ulm, Vöhringen, Günzburg, Bopfingen & Umgebung\n\nE-Mail: eric779fotos@gmail.com\nTelefon & WhatsApp: +49 176 55085770\nInstagram: @leno_fotos779\n\nDies ist eine private Hobby-Website. Verantwortlich für den Inhalt: Eric Leno.\nAlle Fotografien unterliegen dem Urheberrecht. Jede Verwendung bedarf der schriftlichen Zustimmung.",
  },
  datenschutz: {
    title: "Datenschutz",
    body: "Verantwortlich: Eric Leno (Leno Fotografie), Ulm. Kontakt: eric779fotos@gmail.com\n\nWenn du mich über diese Website kontaktierst oder einen Termin anfragst, speichere ich deine Angaben (Name, E-Mail, ggf. Telefonnummer, Wunschtermin und deine Nachricht), um deine Anfrage zu bearbeiten und Termine zu verwalten. Rechtsgrundlage ist Art. 6 Abs. 1 lit. b DSGVO.\n\nFür den Versand der automatischen E-Mails (Bestätigungen, Erinnerungen) nutze ich einen externen E-Mail-Dienstleister. Dabei wird deine E-Mail-Adresse an diesen Dienst übermittelt. Eine sonstige Weitergabe deiner Daten an Dritte findet nicht statt.\n\nDeine Daten lösche ich, sobald sie für die Abwicklung nicht mehr benötigt werden. Du hast jederzeit das Recht auf Auskunft, Berichtigung, Löschung und Widerruf — schreib mir dazu einfach an eric779fotos@gmail.com.",
  },
};

export const Footer = () => {
  const [modal, setModal] = useState(null);

  const toTop = () => {
    const lenis = window.__lenis;
    if (lenis) lenis.scrollTo(0, { duration: 1.6 });
    else window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <footer data-testid="footer" className="border-t border-white/10 bg-ink2">
      <div className="mx-auto max-w-[1440px] px-6 pb-10 pt-16 lg:px-12">
        <div className="flex flex-col justify-between gap-10 lg:flex-row lg:items-end">
          <div>
            <p className="font-mono text-[10px] uppercase tracking-[0.35em] text-gold">Momente · Natürlich · Echt</p>
            <img
              src="/assets/logo-wordmark.png"
              alt="Leno Fotografie"
              data-testid="footer-wordmark"
              className="mt-6 w-full max-w-2xl"
            />
            <p className="mt-6 max-w-md font-serif text-xl italic text-zinc-400">
              Mehr als nur Bilder — es sind Erinnerungen.
            </p>
          </div>
          <button
            data-testid="footer-back-to-top"
            onClick={toTop}
            className="group flex h-14 w-14 shrink-0 items-center justify-center rounded-full border border-white/20 text-white transition-colors hover:border-gold hover:text-gold"
            aria-label="Nach oben"
          >
            <ArrowUp size={18} className="transition-transform group-hover:-translate-y-1" />
          </button>
        </div>

        <div className="mt-12 flex flex-col items-start justify-between gap-6 border-t border-white/10 pt-8 lg:flex-row lg:items-center">
          <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-600">
            © {new Date().getFullYear()} Leno Fotografie — Alle Rechte vorbehalten
          </p>
          <div className="flex flex-wrap items-center gap-6">
            <a
              href="https://instagram.com/leno_fotos779"
              target="_blank"
              rel="noreferrer"
              data-testid="footer-social-instagram"
              className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-400 transition-colors hover:text-gold"
            >
              Instagram · @leno_fotos779
            </a>
            <button data-testid="footer-impressum-button" onClick={() => setModal("impressum")} className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-400 transition-colors hover:text-gold">
              Impressum
            </button>
            <button data-testid="footer-datenschutz-button" onClick={() => setModal("datenschutz")} className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-400 transition-colors hover:text-gold">
              Datenschutz
            </button>
          </div>
        </div>
      </div>

      <AnimatePresence>
        {modal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-ink/90 p-6 backdrop-blur-md"
            onClick={() => setModal(null)}
          >
            <motion.div
              initial={{ y: 24, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              className="w-full max-w-lg border border-white/10 bg-ink2 p-8"
              onClick={(e) => e.stopPropagation()}
              data-testid="footer-legal-modal"
            >
              <div className="flex items-center justify-between">
                <h3 className="font-serif text-2xl text-white">{LEGAL[modal].title}</h3>
                <button onClick={() => setModal(null)} data-testid="footer-legal-close" className="text-zinc-400 hover:text-gold" aria-label="Schließen">
                  <X size={18} />
                </button>
              </div>
              <p className="mt-6 whitespace-pre-line text-sm leading-relaxed text-zinc-400">{LEGAL[modal].body}</p>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </footer>
  );
};
